/**
 * Breadcrumbs — ONE trail per page, ONE source (FRIDGE-269, Phase 1).
 *
 * Every helper below returns the complete trail for a page type as
 * `[{ name, url }]`. The SAME array must feed both surfaces of a page:
 *
 *   visible  → <Breadcrumb trail={trail} />          (components/brand-kit/breadcrumb.tsx)
 *   schema   → buildBreadcrumbSchema(trail)          (lib/structured-data.ts)
 *              or <BreadcrumbJsonLd trail={trail} /> (components/seo/breadcrumb-jsonld.tsx)
 *
 * so the visible names and the BreadcrumbList names cannot disagree — the
 * defect class the 08/15/2026 served-HTML audit found on collections
 * ("Fridge.com / Refrigerators / Refrigerators" visible vs
 * "Fridge.com / Collections / Refrigerators" in schema), PDPs (singular
 * "Refrigerator" → /collections/all visible vs "Counter-Depth Refrigerators"
 * canonical in schema), blog ("Blog" vs "Blogs"), tools ("Tools / Age
 * Calculator" vs "Free Tools / Refrigerator Age Calculator") and local
 * ("Local" vs "Local Fridge Finder").
 *
 * Rules encoded here (docs/Page-Types-And-Templates.md §5/§6):
 *  - The first crumb is always { name: 'Fridge.com', url: '/' }.
 *  - The last crumb is the current page and carries NO url (the visible
 *    renderer never links it; buildBreadcrumbSchema omits `item` for it).
 *  - Collection names are PLURAL, always through formatCollectionName().
 *  - The PDP family crumb is the canonical collection (never singular, never
 *    /collections/all). When no canonical collection maps, the appliance
 *    family (Refrigerators / Freezers) is used when the product's own type or
 *    title names it; otherwise the level is OMITTED rather than invented.
 *  - Section roots use one label everywhere: Collections, Brands, Blog,
 *    Tools, Local Fridge Finder, Utility Rebates, Recycling Guide, Filters,
 *    Recalls, Press, Parts, Data.
 *  - The home page has no trail (a one-item BreadcrumbList is pointless).
 *
 * Pure and dependency-FREE on purpose: no React, no next/*, no Supabase, and
 * no lib/collections — safe in every client bundle. The three catalog trails
 * that need the taxonomy map and the title formatters (products, collections,
 * blog articles) live in lib/breadcrumbs-catalog.ts, built on the same roots
 * and types; keeping them out of this module keeps the 103-family map out of
 * the client bundles of the tool, blog-index, local, filters, press and hub
 * pages (scripts/bundle-budget.mjs).
 *
 * Guards: tests/breadcrumbs-helpers.test.ts (per-helper plural + fallback
 * assertions), tests/breadcrumbs-visible-equals-schema.test.ts (rendered
 * visible names == schema names on one URL per family) and
 * tests/breadcrumbs-source-pin.test.ts (every template imports its trail
 * from here or from the catalog module).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One level of a trail. `url` is a site-relative path; absent on the leaf. */
export interface Crumb {
  name: string
  url?: string
}

/** The whole trail, root first. Empty means "this page renders no breadcrumb". */
export type BreadcrumbTrail = Crumb[]

// ---------------------------------------------------------------------------
// Roots — the ONE label + ONE url for every section. Change a label here and
// every page in the section, visible and schema, changes together.
// ---------------------------------------------------------------------------

export const HOME_CRUMB: Crumb = { name: 'Fridge.com', url: '/' }

export const SECTION_ROOTS = {
  collections: { name: 'Collections', url: '/collections/all' },
  brands: { name: 'Brands', url: '/brands' },
  blog: { name: 'Blog', url: '/blogs/news' },
  blogTags: { name: 'Tags', url: '/blogs/news/tags' },
  tools: { name: 'Tools', url: '/tools' },
  local: { name: 'Local Fridge Finder', url: '/local' },
  rebates: { name: 'Utility Rebates', url: '/rebates' },
  recycling: { name: 'Recycling Guide', url: '/recycling-guide' },
  filters: { name: 'Filters', url: '/filters' },
  recalls: { name: 'Recalls', url: '/recalls' },
  // 'Press' — the owner's FINAL label ruling 08/28/2026: URL + breadcrumb +
  // H1 always match, and the /press URL is immutable (printed on 14 wire
  // releases). It briefly went 'Newsroom' the same day; never rename again
  // without his explicit word.
  press: { name: 'Press', url: '/press' },
  parts: { name: 'Parts', url: '/parts' },
  // The filter STORE root (/filter, singular — FRIDGE-396: a filter is not a
  // part; three families, three roots). Distinct from `filters` above, which
  // is the /filters EDITORIAL filter-hub family (hub_pages rows, registry row
  // 13) and does not move.
  filterStore: { name: 'Filters', url: '/filters' },
  // The per-model filter hubs (FRIDGE-722): one page per OEM filter number
  // under the filter root — `/filters/models/<code>`; the index sits at
  // `/filters/models`. Label = the words the page and the nav use.
  filterModels: { name: 'Filter Numbers', url: '/filters/models' },
  data: { name: 'Data', url: '/data' },
  // The LLM wiki (FRIDGE-191) — one wiki, entity-first, rooted at /wiki.
  wiki: { name: 'Wiki', url: '/wiki' },
} as const satisfies Record<string, Crumb>

/** A section root rendered as the current page (leaf) — same name, no url. */
function leafOf(root: Crumb): Crumb {
  return { name: root.name }
}

function clean(s: string | null | undefined): string {
  return (s ?? '').trim()
}

// ---------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------

/** The home page renders no breadcrumb and emits no BreadcrumbList. */
export function homeBreadcrumbs(): BreadcrumbTrail {
  return []
}

// ---------------------------------------------------------------------------
// Collections
// ---------------------------------------------------------------------------

/** /collections/all — the collections directory (every ?page=N). */
export function collectionsAllBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.collections)]
}

// /collections/[slug] and /products/[slug] — collectionBreadcrumbs() and
// productBreadcrumbs() — live in lib/breadcrumbs-catalog.ts (they need the
// canonical taxonomy map; see the module note above).

// ---------------------------------------------------------------------------
// Brands
// ---------------------------------------------------------------------------

/** /brands — the brand directory. */
export function brandsBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.brands)]
}

/** /brands/[slug] — `Fridge.com / Brands / {Brand}`. */
export function brandBreadcrumbs(brand: { name: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.brands, { name: clean(brand.name) }]
}

// ---------------------------------------------------------------------------
// Blog
// ---------------------------------------------------------------------------

/**
 * /blogs/news — page 1 is the section itself; ?page=N adds a "Page N" leaf
 * under the linked section root (the visible index already did this).
 */
export function blogBreadcrumbs(opts: { page?: number } = {}): BreadcrumbTrail {
  const page = opts.page && opts.page > 1 ? opts.page : 1
  if (page === 1) return [HOME_CRUMB, leafOf(SECTION_ROOTS.blog)]
  return [HOME_CRUMB, SECTION_ROOTS.blog, { name: `Page ${page}` }]
}

// /blogs/news/[slug] — blogArticleBreadcrumbs() — lives in
// lib/breadcrumbs-catalog.ts (it needs formatBlogTitle; see the module note).

/** /blogs/news/tags — the tag directory. */
export function blogTagsBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.blog, leafOf(SECTION_ROOTS.blogTags)]
}

/** Complete topic directories are reached directly from the blog index. */
export function blogDirectoryBreadcrumbs(topic: { name: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.blog, { name: clean(topic.name) }]
}

/** /blogs/news/tags/[tag] — `Fridge.com / Blog / Tags / {Tag}`. */
export function blogTagBreadcrumbs(tag: { name: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.blog, SECTION_ROOTS.blogTags, { name: clean(tag.name) }]
}

// ---------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------

/** /tools — the tools index. */
export function toolsBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.tools)]
}

/**
 * /tools/[tool] — `Fridge.com / Tools / {Full tool name}`. The leaf is the
 * tool's full name (the page H1), never a shortened label.
 */
export function toolBreadcrumbs(tool: { name: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.tools, { name: clean(tool.name) }]
}

// ---------------------------------------------------------------------------
// Local Fridge Finder
// ---------------------------------------------------------------------------

/**
 * City names that read as UI words inside a trail ("Page", "Start") carry
 * their state code so the crumb stays unambiguous — the same rule at every
 * level the city appears on.
 */
const AMBIGUOUS_CITY_LABELS = new Set(['AZ:Page', 'ND:Page', 'NE:Page', 'WV:Page', 'LA:Start'])

export function localCityCrumbName(cityName: string, stateCode?: string | null): string {
  const city = clean(cityName)
  const code = clean(stateCode).toUpperCase()
  return code && AMBIGUOUS_CITY_LABELS.has(`${code}:${city}`) ? `${city}, ${code}` : city
}

/** /local — the index. */
export function localBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.local)]
}

/** /local/[state] — `Fridge.com / Local Fridge Finder / {State}`. */
export function localStateBreadcrumbs(state: { stateName: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.local, { name: clean(state.stateName) }]
}

/** /local/[state]/[city] — `… / {State} / {City}`. */
export function localCityBreadcrumbs(city: {
  stateName: string
  stateSlug: string
  stateCode?: string | null
  cityName: string
}): BreadcrumbTrail {
  return [
    HOME_CRUMB,
    SECTION_ROOTS.local,
    { name: clean(city.stateName), url: `/local/${city.stateSlug}` },
    { name: localCityCrumbName(city.cityName, city.stateCode) },
  ]
}

/**
 * /local/[state]/zip-guides/[letter] — `… / {State} / ZIP guides starting with A`
 * (FRIDGE-1328). The state crumb links back to the state page that indexes the
 * letters; the leaf names the letter range shown.
 */
export function localZipGuideLetterBreadcrumbs(page: {
  stateName: string
  stateSlug: string
  letterLabel: string
}): BreadcrumbTrail {
  return [
    HOME_CRUMB,
    SECTION_ROOTS.local,
    { name: clean(page.stateName), url: `/local/${page.stateSlug}` },
    { name: `ZIP guides ${clean(page.letterLabel)}` },
  ]
}

/** /local/[state]/[city]/[zipcode] — `… / {State} / {City} / ZIP {zipcode}`. */
export function localZipBreadcrumbs(zip: {
  stateName: string
  stateSlug: string
  stateCode?: string | null
  cityName: string
  citySlug: string
  zipcode: string
}): BreadcrumbTrail {
  return [
    HOME_CRUMB,
    SECTION_ROOTS.local,
    { name: clean(zip.stateName), url: `/local/${zip.stateSlug}` },
    { name: localCityCrumbName(zip.cityName, zip.stateCode), url: `/local/${zip.stateSlug}/${zip.citySlug}` },
    { name: `ZIP ${clean(zip.zipcode)}` },
  ]
}

// ---------------------------------------------------------------------------
// Utility Rebates
// ---------------------------------------------------------------------------

/** /rebates — the index. */
export function rebatesBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.rebates)]
}

/** /rebates/[state] — `Fridge.com / Utility Rebates / {State}`. */
export function rebateStateBreadcrumbs(state: { stateName: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.rebates, { name: clean(state.stateName) }]
}

/** /rebates/[state]/[utility] — `… / {State} / {Utility}` (the H1 name). */
export function rebateUtilityBreadcrumbs(utility: {
  stateName: string
  stateSlug: string
  utilityName: string
}): BreadcrumbTrail {
  return [
    HOME_CRUMB,
    SECTION_ROOTS.rebates,
    { name: clean(utility.stateName), url: `/rebates/${utility.stateSlug}` },
    { name: clean(utility.utilityName) },
  ]
}

// ---------------------------------------------------------------------------
// Recycling Guide
// ---------------------------------------------------------------------------

/** /recycling-guide — the index. */
export function recyclingGuideBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.recycling)]
}

/** /recycling-guide/[state] — `Fridge.com / Recycling Guide / {State}`. */
export function recyclingStateBreadcrumbs(state: { stateName: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.recycling, { name: clean(state.stateName) }]
}

// ---------------------------------------------------------------------------
// Hubs (/best-*, /[hub-slug]) and filter hubs (/filters/*)
// ---------------------------------------------------------------------------

/**
 * /[hub-slug] — `Fridge.com / {Hub}`; brand hubs sit under the real Brands
 * directory (`Fridge.com / Brands / {Hub}`). A category level for the other
 * hub types is Phase 2 (it needs the taxonomy ruling, FRIDGE-57) — flat
 * until then, identically on both surfaces.
 */
export function hubBreadcrumbs(hub: { title: string; hub_type?: string | null }): BreadcrumbTrail {
  const leaf: Crumb = { name: clean(hub.title) }
  if (hub.hub_type === 'brand') return [HOME_CRUMB, SECTION_ROOTS.brands, leaf]
  return [HOME_CRUMB, leaf]
}

/** /filters — the filter-hub index. */
export function filtersBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.filters)]
}

/** /about-us/{fin,fpn,ffn,fis} — `Fridge.com / About Us / {CODE}` (the
 *  identifier hub-and-spoke, 08/21/2026: /about-us is the hub). */
export function aboutIdentifierBreadcrumbs(code: 'FIN' | 'FPN' | 'FFN' | 'FIS'): BreadcrumbTrail {
  return [HOME_CRUMB, { name: 'About Us', url: '/about-us' }, { name: code }]
}

/** /filters/[slug] — `Fridge.com / Filters / {Filter hub}`. */
export function filterHubBreadcrumbs(hub: { title: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.filters, { name: clean(hub.title) }]
}

// ---------------------------------------------------------------------------
// Recalls · Press · Parts · Data · static pages
// ---------------------------------------------------------------------------

/** /recalls — the index. */
export function recallsBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.recalls)]
}

/** /recalls/[slug] — `Fridge.com / Recalls / {Recall or brand roll-up}`. */
export function recallBreadcrumbs(recall: { name: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.recalls, { name: clean(recall.name) }]
}

/** /press — the index. */
export function pressBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.press)]
}

/** /press/media-kit — `Fridge.com / Press / Media Kit` (FRIDGE-703). */
export function pressMediaKitBreadcrumbs(): BreadcrumbTrail {
  return [...pressBreadcrumbs(), { name: 'Media Kit' }]
}

/** /press/[slug] — `Fridge.com / Press / {Release title}` (full title). */
export function pressReleaseBreadcrumbs(release: { title: string; slug?: string }): BreadcrumbTrail {
  // Owner 08/28/2026: press breadcrumbs mirror the URL — the leaf is the
  // slug's own words (title-cased), never the long headline. The full title
  // stays the page's H1 and the NewsArticle headline; the trail reads like
  // the path: Fridge.com / Press / Kitchen Climate Divide 2026.
  const leaf = release.slug
    ? release.slug
        .split('-')
        .map((w) => (/^\d/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
        .join(' ')
    : clean(release.title)
  return [HOME_CRUMB, SECTION_ROOTS.press, { name: leaf }]
}

/** /part — `Fridge.com / Parts` (the parts store hub; FRIDGE-389 singular root). */
export function partsBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.parts)]
}

/** /filter — `Fridge.com / Filters` (the filter store hub; FRIDGE-396 singular root). */
export function filterStoreBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.filterStore)]
}

/** /filters/models — `Fridge.com / Filters / Filter Numbers` (the per-model hub index, FRIDGE-722). */
export function filterModelsBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.filterStore, leafOf(SECTION_ROOTS.filterModels)]
}

/** /filters/cross-reference — `Fridge.com / Filters / Filter Numbers / Cross-Reference` (FRIDGE-731). */
export function filterCrossReferenceBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.filterStore, SECTION_ROOTS.filterModels, { name: 'Cross-Reference' }]
}

/** /filters/models/[code] — `Fridge.com / Filters / Filter Numbers / {Brand CODE}`. */
export function filterModelHubBreadcrumbs(model: { brand: string; code: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.filterStore, SECTION_ROOTS.filterModels, { name: clean(`${model.brand} ${model.code}`) }]
}

/**
 * /data and /data/[page] — `Fridge.com / Data [/ {Page}]`. Provided for the
 * data lane (app/data/** is owned there); the portal passes no leaf.
 */
export function dataBreadcrumbs(page: { leaf?: string | null } = {}): BreadcrumbTrail {
  const leaf = clean(page.leaf)
  return leaf ? [HOME_CRUMB, SECTION_ROOTS.data, { name: leaf }] : [HOME_CRUMB, leafOf(SECTION_ROOTS.data)]
}

/** A one-level static page directly under home — `Fridge.com / {Page}`. */
export function staticPageBreadcrumbs(page: { name: string }): BreadcrumbTrail {
  return [HOME_CRUMB, { name: clean(page.name) }]
}

/** /wiki — the entity wiki index (FRIDGE-191). */
export function wikiBreadcrumbs(): BreadcrumbTrail {
  return [HOME_CRUMB, leafOf(SECTION_ROOTS.wiki)]
}

/** /wiki/[entity] — `Fridge.com / Wiki / {Entity plural}`. */
export function wikiEntityBreadcrumbs(entity: { plural: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.wiki, { name: clean(entity.plural) }]
}

// ---------------------------------------------------------------------------
// Utilities shared by the renderer, the schema builder and the guards
// ---------------------------------------------------------------------------

/** A trail is renderable (visible or schema) only when it has ≥ 2 levels. */
export function isRenderableTrail(trail: BreadcrumbTrail | null | undefined): trail is BreadcrumbTrail {
  return Array.isArray(trail) && trail.length >= 2
}

/** The names, root first — what the visible trail shows and the schema lists. */
export function trailNames(trail: BreadcrumbTrail): string[] {
  return trail.map((c) => c.name)
}

/** Standalone editorial policy pages remain connected to the About hub. */
export function editorialPolicyBreadcrumbs(page: 'Editorial standards'): BreadcrumbTrail {
  return [HOME_CRUMB, { name: 'About Us', url: '/about-us' }, { name: page }]
}
